import InfiniteList from 'components/InfiniteList'
import { groupKeyFormat, calendarFormats } from 'lib/moment'
import moment from 'moment-timezone'
import {
  FilterableEngagementField,
  SortableEngagementField,
  SortDirection,
  useDiscoverEngagementsQuery
} from 'graphql/generated'
import { Typography } from 'components/common'
import React from 'react'
import EventListItem from 'components/discover/EventListItem'
import { useQueryErrorHandler } from 'hooks'

interface IProps {
  slug: string
}

function EventSeriesList({ slug }: IProps) {
  const onError = useQueryErrorHandler(
    'Failed to load Events for this series. Please try again later.'
  )
  const { data, loading, networkStatus, fetchMore } =
    useDiscoverEngagementsQuery({
      variables: {
        seriesSlug: slug,
        engagementConnection: { first: 10 },
        sort: {
          direction: SortDirection.ASCENDING,
          field: SortableEngagementField.START_TIME
        },
        filters: [
          FilterableEngagementField.STATUS_NOT_ENDED,
          FilterableEngagementField.TYPE_SOCIAL
        ]
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onError
    })

  const edges = data?.discoverEngagements?.edges
  const pageInfo = data?.discoverEngagements?.pageInfo
  const loadingMore = networkStatus === 3

  const loadMore = () => {
    if (!pageInfo || !pageInfo.hasNextPage) {
      return
    } else if (loading || loadingMore) {
      return
    }

    fetchMore({
      variables: {
        engagementConnection: {
          first: 10,
          after: pageInfo.endCursor
        },
        direction: SortDirection.ASCENDING,
        field: SortableEngagementField.START_TIME
      }
    })
  }

  return (
    <div className="space-y-4 bg-white shadow p-4 rounded-lg">
      <div className="bg-gray-50 p-4 rounded">
        <div className="flex flex-1 justify-between">
          <Typography>Event Series</Typography>
        </div>

        <InfiniteList
          className="relative overflow-visible space-y-2 px-2"
          items={edges}
          loadMore={loadMore}
          loading={loading}
          loadingMore={loadingMore}
          renderItem={(edge, i) => (
            <EventListItem index={i} event={edge.node} key={edge.node.id} />
          )}
          emptyText={'No Upcoming Events Scheduled'}
          groupBy={val => moment(val.node.startTime).format(groupKeyFormat)}
          renderSectionHeader={groupKey => (
            <div
              className="z-75 sticky top-16 w-3/4 mx-auto"
              key={`section-${groupKey}`}
            >
              <div
                className="absolute inset-0 flex items-center"
                style={{
                  background:
                    'linear-gradient(to bottom,rgba(255, 255, 255, 0),white,rgba(255, 255, 255, 0))'
                }}
              >
                <div className="w-full border-t border-gray-300" />
              </div>

              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-xs text-gray-400 rounded-full overflow-hidden">
                  {moment(groupKey, groupKeyFormat).calendar(
                    null,
                    calendarFormats
                  )}
                </span>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  )
}

export default EventSeriesList
